import { useState } from 'react';
import type { AdminMessage } from '../../api/types.js';
import { formatCny, outboundTurnCost } from '../../lib/kimi-k26-cost.js';
import { isRecalledMessage, replySourceText, statusText } from '../../lib/utils.js';
import { MessageMedia, MessageText } from './MessageMedia.js';
import { Avatar } from '../ui/Avatar.js';

interface MessageBubbleProps {
  message: AdminMessage;
  conversationName: string;
  botName: string;
  feedbackButton: React.ReactNode;
}

export function MessageBubble({ message, conversationName, botName, feedbackButton }: MessageBubbleProps) {
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const isInternalHandoff = message.mediaMetadata?.internalEventType === 'handoff';
  const isOutgoing = message.direction === 'out';
  const isRecalled = isRecalledMessage(message);
  const sourceText = replySourceText(message);
  const turnCost = isOutgoing ? outboundTurnCost(message) : null;
  const statusClass =
    message.status === 'failed'
      ? 'message-status-failed'
      : message.status === 'pending'
        ? 'message-status-pending'
        : '';

  return (
    <div
      className={[
        'chat-message',
        isOutgoing ? 'chat-message-outgoing' : 'chat-message-incoming',
        isInternalHandoff ? 'message-internal-event' : '',
        isRecalled ? 'message-recalled' : '',
      ].filter(Boolean).join(' ')}
    >
      {!isOutgoing ? <Avatar name={conversationName} size="sm" /> : null}
      <div className="chat-message-content">
        <MessageMedia message={message} />
        {isRecalled ? <div className="message-recalled-label">已撤回</div> : null}
        <MessageText message={message} />
        {isOutgoing && message.thinking ? (
          <>
            <div className="thinking-toggle" onClick={() => setThinkingOpen((v) => !v)}>
              {thinkingOpen ? '🧠 思考（收起）' : '🧠 思考'}
            </div>
            {thinkingOpen ? <div className="thinking-content">{message.thinking}</div> : null}
          </>
        ) : null}
        <div className="message-footer">
          {sourceText ? <span className="reply-source-badge">{sourceText}</span> : null}
          {turnCost ? (
            <span className="message-ai-cost" title={turnCost.title}>
              {formatCny(turnCost.cny)}
            </span>
          ) : null}
          <span className={`message-meta ${statusClass}`}>
            {statusText(message)} ·{' '}
            {new Date(message.createdAt || '').toLocaleTimeString('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {isInternalHandoff || isRecalled ? null : feedbackButton}
        </div>
      </div>
      {isOutgoing ? <Avatar name={botName} size="sm" /> : null}
    </div>
  );
}
